import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, LineChart, Line, CartesianGrid,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Palette that matches the app's navy/bronze/emerald identity
const COLORS = {
  navy: '#0F172A',
  bronze: '#B45309',
  emerald: '#059669',
  muted: '#94A3B8',
  destructive: '#DC2626',
};

/**
 * CaseStatusChart — pie chart of cases broken down by status.
 * Uses the stats payload from /dashboard/stats; no extra fetch.
 */
export function CaseStatusChart({ cases }) {
  const { language } = useLanguage();
  const lbl = (es, en) => (language === 'es' ? es : en);

  const data = [
    { name: lbl('Abiertos', 'Open'), value: cases?.open || 0, color: COLORS.emerald },
    { name: lbl('Pendientes', 'Pending'), value: cases?.pending || 0, color: COLORS.bronze },
    { name: lbl('Cerrados', 'Closed'), value: cases?.closed || 0, color: COLORS.muted },
    { name: lbl('Archivados', 'Archived'), value: cases?.archived || 0, color: COLORS.navy },
  ].filter(d => d.value > 0);

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <Card className="card-base">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-serif">
          {lbl('Casos por estado', 'Cases by status')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {lbl('Sin casos todavía', 'No cases yet')}
          </div>
        ) : (
          <div className="h-56" role="img" aria-label={lbl(
            `Gráfica circular de casos por estado. Total ${total} casos.`,
            `Pie chart of cases by status. Total ${total} cases.`,
          )}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  innerRadius={40} outerRadius={72} paddingAngle={2}>
                  {data.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 6 }}
                  formatter={(value, name) => [value, name]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
        <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          {data.map((d) => (
            <li key={d.name} className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: d.color }} />
              <span className="text-muted-foreground flex-1">{d.name}</span>
              <span className="font-medium tabular-nums">{d.value}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/**
 * BillableHoursChart — billable vs non-billable hours as a donut.
 */
export function BillableHoursChart({ time }) {
  const { language } = useLanguage();
  const lbl = (es, en) => (language === 'es' ? es : en);

  const billable = Number(time?.billable_hours || 0);
  const nonBillable = Math.max(0, Number(time?.total_hours || 0) - billable);
  const data = [
    { name: lbl('Facturables', 'Billable'), value: billable, color: COLORS.emerald },
    { name: lbl('No facturables', 'Non-billable'), value: nonBillable, color: COLORS.muted },
  ].filter(d => d.value > 0);
  const total = data.reduce((s, d) => s + d.value, 0);
  const pct = total > 0 ? Math.round((billable / total) * 100) : 0;

  return (
    <Card className="card-base">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-serif">
          {lbl('Horas facturables', 'Billable hours')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {lbl('Sin horas registradas todavía', 'No hours logged yet')}
          </div>
        ) : (
          <>
            <div className="h-40 relative" role="img" aria-label={lbl(
              `${pct}% de horas facturables de ${total.toFixed(1)} totales.`,
              `${pct}% billable hours out of ${total.toFixed(1)} total.`,
            )}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    innerRadius={48} outerRadius={72} paddingAngle={2}>
                    {data.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }}
                    formatter={(v, n) => [`${v} h`, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <div className="text-2xl font-serif font-bold text-navy dark:text-foreground">{pct}%</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {lbl('Facturable', 'Billable')}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
              <span>{lbl('Facturables', 'Billable')}: <b className="text-foreground">{billable.toFixed(1)} h</b></span>
              <span>{lbl('No facturables', 'Non-billable')}: <b className="text-foreground">{nonBillable.toFixed(1)} h</b></span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * RevenueTrendChart — line chart of paid-invoice revenue per month.
 * Fetches /dashboard/revenue-trend on mount.
 */
export function RevenueTrendChart({ months = 6 }) {
  const { getAuthHeaders } = useAuth();
  const { language } = useLanguage();
  const lbl = (es, en) => (language === 'es' ? es : en);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/dashboard/revenue-trend?months=${months}`, {
      headers: getAuthHeaders(), credentials: 'include',
    })
      .then(r => r.ok ? r.json() : [])
      .then(setData)
      .catch(() => setData([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months]);

  const total = data.reduce((s, d) => s + (d.revenue || 0), 0);

  return (
    <Card className="card-base">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-serif">
            {lbl(`Ingresos últimos ${months} meses`, `Revenue last ${months} months`)}
          </CardTitle>
          <span className="text-sm font-medium text-emerald">${total.toLocaleString()}</span>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-48 animate-pulse bg-muted rounded" />
        ) : total === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {lbl('Aún no hay pagos registrados', 'No payments recorded yet')}
          </div>
        ) : (
          <div className="h-48" role="img" aria-label={lbl(
            `Tendencia de ingresos de los últimos ${months} meses.`,
            `Revenue trend for the last ${months} months.`,
          )}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 6 }}
                  formatter={(v) => [`$${Number(v).toLocaleString()}`, lbl('Ingreso', 'Revenue')]}
                />
                <Line type="monotone" dataKey="revenue" stroke={COLORS.emerald} strokeWidth={2}
                  dot={{ fill: COLORS.emerald, r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * InvoiceStatusChart — horizontal bar chart of invoice counts by status.
 */
export function InvoiceStatusChart({ invoices }) {
  const { language } = useLanguage();
  const lbl = (es, en) => (language === 'es' ? es : en);

  const data = [
    { name: lbl('Borrador', 'Draft'), value: invoices?.draft || 0, color: COLORS.muted },
    { name: lbl('Enviadas', 'Sent'), value: invoices?.sent || 0, color: COLORS.bronze },
    { name: lbl('Pagadas', 'Paid'), value: invoices?.paid || 0, color: COLORS.emerald },
  ];
  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <Card className="card-base">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-serif">
          {lbl('Facturas por estado', 'Invoices by status')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {total === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {lbl('Aún no hay facturas', 'No invoices yet')}
          </div>
        ) : (
          <div className="h-40" role="img" aria-label={lbl(
            'Gráfica de barras de facturas por estado.',
            'Bar chart of invoices by status.',
          )}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={70} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {data.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
